import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import staioIcon from "@/assets/images/staio-icon.png";
import {
  BarChart3,
  Calendar,
  CreditCard,
  Globe,
  Sparkles,
  Shield,
  Users,
  ArrowRight,
  CheckCircle2,
  Building2,
  ClipboardList,
  Wrench,
  Bell,
  Key,
  DoorOpen,
  Lock,
  MessageSquare,
  DollarSign,
  Zap,
  TrendingUp,
  FileText,
} from "lucide-react";

const featureRows = [
  {
    label: "Core Operations",
    items: [
      { icon: Calendar, title: "Reservation Management", desc: "Timeline views, calendar sync, overbooking prevention, and iCal export for seamless scheduling." },
      { icon: Globe, title: "Booking Engine", desc: "Public booking pages, rate plans, availability search, and multi-step checkout for direct bookings." },
      { icon: DoorOpen, title: "Channel Manager", desc: "Airbnb, Booking.com, VRBO, and Google Vacation integration via iCal sync." },
      { icon: Key, title: "Smart Locks", desc: "Generate guest access codes with automatic validity periods and remote revocation." },
    ],
  },
  {
    label: "Team & Operations",
    items: [
      { icon: ClipboardList, title: "Automated Cleaning", desc: "Auto-created jobs on checkout, smart priority based on upcoming reservations, completion tracking." },
      { icon: Wrench, title: "Maintenance Management", desc: "Job tracking, cost logging, contractor assignment, and status updates across properties." },
      { icon: Users, title: "Team Roles", desc: "Admin, Property Managers, Cleaners, Maintenance, Affiliates \u2014 each with a dedicated portal." },
      { icon: Bell, title: "Push Notifications", desc: "Real-time alerts for bookings, cleanings, maintenance, and support tickets." },
    ],
  },
  {
    label: "Finance",
    items: [
      { icon: BarChart3, title: "Financial Tracking", desc: "Revenue tracking, commission splits, payment reconciliation, and financial dashboards." },
      { icon: DollarSign, title: "Cash Box (Caja)", desc: "Physical cash flow tracking, deposits, settlements, and reconciliation tools." },
      { icon: FileText, title: "Contractor Invoices", desc: "Weekly invoices with approval workflows: Draft \u2192 Pending \u2192 Approved \u2192 Paid." },
      { icon: Shield, title: "Tax Compliance", desc: "W-9 collection, encrypted storage, 1099 processing, and full audit trails." },
    ],
  },
  {
    label: "Intelligence & Automation",
    items: [
      { icon: MessageSquare, title: "Guest CRM", desc: "Pipeline management with 6 stages, contact tagging, priority tracking, notes, and multi-channel communication." },
      { icon: Zap, title: "Automation Engine", desc: "Rule-based workflows with 8 triggers and 6 actions. Auto-assign cleaners, send emails, create tickets on events." },
      { icon: Sparkles, title: "Lumi AI Assistant", desc: "AI-powered business intelligence with daily briefings, anomaly detection, smart suggestions, and natural language queries." },
      { icon: BarChart3, title: "Forecasting & Analytics", desc: "Revenue forecasting, occupancy predictions, P&L analysis, and dual business mode dashboards." },
    ],
  },
  {
    label: "Growth",
    items: [
      { icon: Building2, title: "Marketplace Listing", desc: "List properties on the public marketplace with photos, amenities, and competitive rates." },
      { icon: CreditCard, title: "Commission & Affiliates", desc: "Multi-party commissions, affiliate referral codes, and automated tracking." },
      { icon: Sparkles, title: "Upsells & Add-ons", desc: "Early check-in, late checkout, experiences, transport \u2014 boost revenue per booking." },
      { icon: Zap, title: "Custom Fields", desc: "Define custom data per apartment or reservation to fit your unique workflow." },
    ],
  },
];

const guestFeatures = [
  { icon: DoorOpen, text: "Digital check-in with photo verification" },
  { icon: Globe, text: "Bilingual property guides (EN/ES)" },
  { icon: MessageSquare, text: "AI-powered chatbot for guest questions" },
  { icon: CheckCircle2, text: "Checkout checklist with photo submission" },
  { icon: ClipboardList, text: "Support ticket system" },
  { icon: DollarSign, text: "Security deposit management" },
];

const securityFeatures = [
  "AES-256-GCM encryption for tax data",
  "Role-based access control",
  "Audit logging for all sensitive data access",
  "Multi-tenant data isolation",
  "IRS-compliant data retention policies",
];

export default function MarketplaceHosts() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    propertyCount: "",
    location: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch("/api/host-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) setSubmitted(true);
    } catch (err) {}
    setSubmitting(false);
  };

  const update = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3 h-16">
          <Link href="/marketplace">
            <div className="flex items-center gap-2 cursor-pointer" data-testid="link-logo">
              <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Staio</span>
            </div>
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <Link href="/marketplace">
              <Button variant="ghost" size="sm" data-testid="link-browse">Browse Properties</Button>
            </Link>
            <Link href="/about">
              <Button variant="outline" size="sm" data-testid="link-about">About</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative py-20 sm:py-28 px-4 bg-gradient-to-br from-primary/10 via-background to-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-hero-title"
          >
            Everything You Need to Manage Rental Properties
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-10" data-testid="text-hero-subtitle">
            Staio replaces your spreadsheets, multiple software tools, and manual processes with one powerful platform. From a single apartment to hundreds of units.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button size="lg" data-testid="button-get-started" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              data-testid="button-see-features"
              onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            >
              See Features Below
            </Button>
          </div>
        </div>
      </section>

      <section id="features" className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-center mb-3"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-features-title"
          >
            Complete Feature Suite
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            20+ powerful modules working together so you can run your rental business from a single dashboard.
          </p>

          {featureRows.map((row) => (
            <div key={row.label} className="mb-10">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4" data-testid={`text-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {row.label}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {row.items.map((item) => (
                  <Card key={item.title} data-testid={`card-feature-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <CardContent className="p-5">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-center mb-3"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-guest-experience-title"
          >
            Guest Experience
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Delight your guests from booking to checkout with a seamless digital experience.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {guestFeatures.map((f) => (
                <div key={f.text} className="flex items-start gap-3" data-testid={`text-guest-${f.text.slice(0, 20).toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <f.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm pt-2">{f.text}</span>
                </div>
              ))}
            </div>
            <Card>
              <CardContent className="p-8 flex flex-col items-center justify-center text-center h-full">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Guest Satisfaction Drives Your Success
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Happy guests leave better reviews, return more often, and recommend your properties. Staio gives you the tools to deliver a five-star experience every time.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6 mx-auto">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold mb-3"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-security-title"
          >
            Enterprise-Grade Security
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto">
            Your data and your guests' data are protected with industry-leading security measures.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {securityFeatures.map((feat) => (
              <div key={feat} className="flex items-start gap-2 text-left" data-testid={`text-security-${feat.slice(0, 15).toLowerCase().replace(/\s+/g, "-")}`}>
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span className="text-sm">{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-center mb-3"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-growth-title"
          >
            Built for Growth
          </h2>
          <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
            Whether you manage one unit or a thousand, Staio scales with you.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "Scale", desc: "From 1 to 1,000+ units on the same platform" },
              { icon: Zap, title: "Control", desc: "22+ feature flags to customize per organization" },
              { icon: BarChart3, title: "Insight", desc: "Comprehensive reporting and analytics dashboard" },
            ].map((item) => (
              <Card key={item.title} data-testid={`card-growth-${item.title.toLowerCase()}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 sm:py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-center mb-3"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-contact-title"
          >
            Get In Touch
          </h2>
          <p className="text-center text-muted-foreground mb-10">
            Tell us about your properties and we'll show you how Staio can help.
          </p>

          {submitted ? (
            <Card data-testid="card-success">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                  <CheckCircle2 className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="text-success-heading">
                  Thank You!
                </h3>
                <p className="text-muted-foreground" data-testid="text-success-message">
                  Our team will reach out within 24 hours to discuss your property management needs.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="form-contact">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        required
                        value={formData.fullName}
                        onChange={(e) => update("fullName", e.target.value)}
                        placeholder="John Doe"
                        data-testid="input-fullname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company / Property Management Name</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder="Acme Properties"
                        data-testid="input-company"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="propertyCount">Number of Properties</Label>
                      <Select
                        value={formData.propertyCount}
                        onValueChange={(val) => update("propertyCount", val)}
                      >
                        <SelectTrigger data-testid="select-property-count">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-5">1-5</SelectItem>
                          <SelectItem value="6-20">6-20</SelectItem>
                          <SelectItem value="21-50">21-50</SelectItem>
                          <SelectItem value="50+">50+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location / City</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => update("location", e.target.value)}
                        placeholder="Miami, FL"
                        data-testid="input-location"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Tell us about your properties and what you're looking for..."
                      rows={4}
                      data-testid="input-message"
                    />
                  </div>
                  <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="button-submit">
                    {submitting ? "Submitting..." : "Request a Demo"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <footer className="border-t bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={staioIcon} alt="Staio" className="w-6 h-6 rounded-md" />
              <span className="text-sm font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Staio</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <Link href="/marketplace"><span className="cursor-pointer hover:text-foreground">Browse</span></Link>
              <Link href="/for-hosts"><span className="cursor-pointer hover:text-foreground">For Hosts</span></Link>
              <Link href="/about"><span className="cursor-pointer hover:text-foreground">About</span></Link>
            </div>
            <p className="text-xs text-muted-foreground">Powered by Staio</p>
          </div>
        </div>
      </footer>
    </div>
  );
}